import { useAuth } from './auth/AppAuth';
import { datadogRum } from '@datadog/browser-rum';
import { useUnleashContext } from '@unleash/proxy-client-react';
import { useEffect } from 'react';
import { AxiosError } from 'axios';
import { isMobile } from 'react-device-detect';
import { Outlet, useNavigate } from 'react-router-dom';
import { useIdleLogout } from './components/hooks/useIdleLogout';
import { useTokenRefresh } from './components/hooks/useTokenRefresh';
import { queryClient } from './data/api-config';
import { useGlobalState } from './data/Global.state';
import { useQueryParam } from './utils/use-query-param';
import { isDtiHosted, isRevProHosted, isTKTEdgeHosted, isVictoryLiveHosted } from './utils/whitelabel-consts';
import { prefersDesktopSite } from './utils/mobile-preferences';

const AUTH_ERRORS = [401, 403];

export function WrapperProviders() {
  const { isAuthenticated, user } = useAuth();
  const updateUnleashContext = useUnleashContext();
  const { handleAccessTokenSilently } = useTokenRefresh();
  const dti = useQueryParam<'yes' | 'no' | undefined>('dti', undefined);
  const navigate = useNavigate();
  const { tenants } = useGlobalState('tenants');

  useIdleLogout();

  useEffect(() => {
    if (dti && dti !== localStorage.getItem('use-dti-login')) {
      localStorage.setItem('use-dti-login', `${dti}`);
      navigate(window.location.pathname);
    }
  }, [dti, navigate]);

  useEffect(() => {
    document.documentElement.setAttribute(
      'data-color-theme',
      isDtiHosted ? 'dti' : isRevProHosted ? 'revpro' : isTKTEdgeHosted ? 'tktedge' : isVictoryLiveHosted ? 'victorylive' : 'bn',
    );
    return () => {
      document.documentElement.removeAttribute('data-color-theme');
    };
  }, []);

  useEffect(() => {
    queryClient.setMutationDefaults([], {
      onError: (error: unknown) => {
        const axiosError = error as AxiosError;

        if (AUTH_ERRORS.includes(axiosError.response?.status || 0)) {
          void handleAccessTokenSilently();
        }
      },
    });
  }, [handleAccessTokenSilently]);

  useEffect(() => {
    if (isAuthenticated) {
      updateUnleashContext({ userId: user?.email });
      handleAccessTokenSilently();
      datadogRum.setUser({
        id: user?.sub,
        email: user?.email,
      });
    }
  }, [user, isAuthenticated, updateUnleashContext, handleAccessTokenSilently]);

  if (tenants !== undefined && tenants.length > 0 && isMobile && !prefersDesktopSite() && !location.pathname.startsWith('/mobile') && !location.pathname.startsWith('/auth')) {
    if (location.search) {
      navigate(`/mobile${location.search}`);
    } else {
      navigate('/mobile');
    }
  }
  return <Outlet />;
}
